import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { StandaloneSidebar } from './StandaloneSidebar';
import { MobileHeader } from './MobileHeader';
import { AppFooter } from './AppFooter';
import { FloatingFeedbackButton } from '@/features/feedback/components/FloatingFeedbackButton';
import { FeedbackDialog } from '@/features/feedback/components/FeedbackDialog';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { Toaster } from '@/components/ui/toaster';

interface LayoutProps {
  children: React.ReactNode;
}

/* eslint-disable max-lines-per-function, complexity */
export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const { user: authUser, userProfile } = useAuth();

  const feedbackConversationId = searchParams.get('feedback_conversation') || undefined;

  // Open feedback dialog when URL contains feedback_conversation and user is authenticated
  useEffect(() => {
    if (
      feedbackConversationId &&
      authUser &&
      location.pathname !== '/auth' &&
      location.pathname !== '/auth/callback'
    ) {
      setIsFeedbackDialogOpen(true);
    }
  }, [feedbackConversationId, authUser?.id, location.pathname]);

  const handleCloseFeedbackDialog = () => {
    setIsFeedbackDialogOpen(false);
    if (feedbackConversationId) {
      const next = new URLSearchParams(searchParams);
      next.delete('feedback_conversation');
      const search = next.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '' },
        { replace: true }
      );
    }
  };

  // Get user data from auth context
  const user = {
    email: authUser?.email || '',
    name:
      userProfile && userProfile.firstName && userProfile.lastName
        ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
        : authUser?.email?.split('@')[0] || 'User',
    role: 'user', // You can get this from userProfile or user roles
    approved: true // You can get this from userProfile or user status
  };

  // Hide layout on auth pages
  if (location.pathname === '/auth' || location.pathname === '/auth/callback') {
    return children;
  }

  const isTestingSite = window.location.hostname === 'vehixv2.pages.dev';

  // Pages that should have fixed footer with scrollable content.
  const fixedFooterRoutes = [
    '/routing/manual',
    '/routing/preview',
    '/route-tracker',
    '/recalls',
    '/recall-map',
    '/admin/feedback',
    '/admin/analytics',
    '/admin/control-center'
  ];
  const hasFixedFooter =
    location.pathname === '/' ||
    fixedFooterRoutes.some(route => location.pathname.startsWith(route));

  return (
    <div className='h-screen bg-background flex flex-col overflow-hidden'>
      {/* Testing Environment Banner */}
      {isTestingSite && (
        <div className='bg-amber-500 text-white text-center py-1 text-sm font-medium flex-shrink-0'>
          🧪 Testing Environment - New Design Preview
        </div>
      )}

      {/* Mobile Header */}
      <div className='flex-shrink-0'>
        <MobileHeader onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
      </div>

      <div className='flex flex-1 overflow-hidden'>
        <StandaloneSidebar
          user={user}
          isMobileMenuOpen={isMobileMenuOpen}
          onMobileMenuToggle={setIsMobileMenuOpen}
        />
        <main
          className={`flex-1 w-full flex flex-col ${
            hasFixedFooter ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'
          }`}
        >
          <div
            className={
              hasFixedFooter
                ? 'flex-1 min-h-0 overflow-y-auto overflow-x-hidden flex flex-col'
                : 'contents'
            }
          >
            {/* Error Banner Slot - for global/system errors */}
            <div id='error-banner-slot' className='flex-shrink-0 px-2 sm:px-4 md:px-6 pt-2 lg:pt-2'>
              {/* Error banners will be rendered here via portal or context */}
            </div>
            <div
              className={`${
                hasFixedFooter ? 'flex-1 min-h-0 flex flex-col' : 'flex-none min-w-0'
              } ${
                location.pathname.includes('/routing/manual')
                  ? 'p-1'
                  : location.pathname.includes('/routing')
                    ? 'px-2 sm:px-4 md:px-6'
                    : 'px-2 sm:px-4 md:px-6'
              }`}
            >
              {children}
            </div>
          </div>
          <AppFooter sticky={hasFixedFooter} className={hasFixedFooter ? 'flex-shrink-0' : ''} />
        </main>
      </div>
      <Toaster />

      {/* Floating Feedback Button */}
      <FloatingFeedbackButton
        onOpenDialog={() => setIsFeedbackDialogOpen(true)}
        isDialogOpen={isFeedbackDialogOpen}
      />

      {/* Feedback Dialog */}
      <FeedbackDialog
        isOpen={isFeedbackDialogOpen}
        onClose={handleCloseFeedbackDialog}
        {...(feedbackConversationId && { initialConversationId: feedbackConversationId })}
      />
    </div>
  );
};
